import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Paper,
  Chip,
} from '@mui/material';
import {
  AddPhotoAlternate,
  Delete,
  DragIndicator,
  Star,
  StarBorder,
} from '@mui/icons-material';
import {
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db, storage } from '../../services/firebase';

interface VenueImage {
  url: string;
  path: string;
  order: number;
  uploadedAt: string;
  isPrimary?: boolean;
}

interface VenueImageManagerProps {
  venueId: string;
  ownerId: string;
  existingImages?: VenueImage[];
  googlePhotoUrl?: string; // fallback shown when no portal images
  onImagesUpdated?: (images: VenueImage[]) => void;
}

const MAX_IMAGES = 6;
const MAX_FILE_SIZE_MB = 5;

const VenueImageManager: React.FC<VenueImageManagerProps> = ({
  venueId,
  ownerId,
  existingImages = [],
  googlePhotoUrl,
  onImagesUpdated,
}) => {
  const [images, setImages] = useState<VenueImage[]>(existingImages);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deletingPath, setDeletingPath] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    if (images.length + files.length > MAX_IMAGES) {
      setError(`You can upload a maximum of ${MAX_IMAGES} images.`);
      return;
    }

    setError(null);
    setUploading(true);
    setUploadProgress(0);

    const newImages: VenueImage[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setError(`${file.name} is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
        continue;
      }

      if (!file.type.startsWith('image/')) {
        setError(`${file.name} is not a valid image file.`);
        continue;
      }

      try {
        const timestamp = Date.now();
        const path = `venues/${venueId}/images/${timestamp}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
        const fileRef = storageRef(storage, path);

        await new Promise<void>((resolve, reject) => {
          const uploadTask = uploadBytesResumable(fileRef, file);
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = ((i + snapshot.bytesTransferred / snapshot.totalBytes) / files.length) * 100;
              setUploadProgress(Math.round(progress));
            },
            reject,
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              newImages.push({
                url,
                path,
                order: images.length + newImages.length,
                uploadedAt: new Date().toISOString(),
                isPrimary: images.length === 0 && newImages.length === 0,
              });
              resolve();
            }
          );
        });
      } catch (err) {
        console.error('Upload error:', err);
        setError(`Failed to upload ${file.name}. Please try again.`);
      }
    }

    if (newImages.length > 0) {
      // Save to Firestore
      const updatedImages = [...images, ...newImages];
      try {
        await updateDoc(doc(db, 'venues', venueId), {
          'profile.images': updatedImages,
          // Set primary image as coverImageUrl for quick access
          'profile.coverImageUrl': updatedImages.find(i => i.isPrimary)?.url || updatedImages[0].url,
        });
        setImages(updatedImages);
        onImagesUpdated?.(updatedImages);
        setSuccess(`${newImages.length} image${newImages.length > 1 ? 's' : ''} uploaded successfully.`);
      } catch (err) {
        setError('Images uploaded but failed to save. Please refresh and try again.');
      }
    }

    setUploading(false);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [images, venueId, onImagesUpdated]);

  const handleDelete = useCallback(async (image: VenueImage) => {
    setDeletingPath(image.path);
    setError(null);
    try {
      // Delete from Storage
      await deleteObject(storageRef(storage, image.path));

      // Update Firestore
      const updatedImages = images
        .filter(i => i.path !== image.path)
        .map((i, idx) => ({ ...i, order: idx }));

      // If deleted image was primary, make first remaining image primary
      if (image.isPrimary && updatedImages.length > 0) {
        updatedImages[0].isPrimary = true;
      }

      await updateDoc(doc(db, 'venues', venueId), {
        'profile.images': updatedImages,
        'profile.coverImageUrl': updatedImages.find(i => i.isPrimary)?.url || updatedImages[0]?.url || null,
      });

      setImages(updatedImages);
      onImagesUpdated?.(updatedImages);
      setSuccess('Image deleted.');
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete image. Please try again.');
    } finally {
      setDeletingPath(null);
    }
  }, [images, venueId, onImagesUpdated]);

  const handleSetPrimary = useCallback(async (image: VenueImage) => {
    const updatedImages = images.map(i => ({
      ...i,
      isPrimary: i.path === image.path,
    }));
    try {
      await updateDoc(doc(db, 'venues', venueId), {
        'profile.images': updatedImages,
        'profile.coverImageUrl': image.url,
      });
      setImages(updatedImages);
      onImagesUpdated?.(updatedImages);
      setSuccess('Primary image updated.');
    } catch (err) {
      setError('Failed to update primary image.');
    }
  }, [images, venueId, onImagesUpdated]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Venue Photos</Typography>
          <Typography variant="body2" color="text.secondary">
            Upload your own photos to replace Google images in the JamApp. Up to {MAX_IMAGES} photos, max {MAX_FILE_SIZE_MB}MB each.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <AddPhotoAlternate />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || images.length >= MAX_IMAGES}
          sx={{ bgcolor: '#F24F2F', '&:hover': { bgcolor: '#d63d1e' }, whiteSpace: 'nowrap' }}
        >
          {uploading ? `Uploading ${uploadProgress}%` : 'Add Photos'}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Image grid */}
      {images.length > 0 ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 2, mb: 2 }}>
          {images.map((image) => (
            <Paper
              key={image.path}
              elevation={image.isPrimary ? 3 : 1}
              sx={{
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                border: image.isPrimary ? '2px solid #F24F2F' : '2px solid transparent',
                aspectRatio: '4/3',
              }}
            >
              <img
                src={image.url}
                alt="Venue"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />

              {/* Primary badge */}
              {image.isPrimary && (
                <Chip
                  label="Primary"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 6,
                    left: 6,
                    bgcolor: '#F24F2F',
                    color: 'white',
                    fontSize: '0.7rem',
                    height: 20,
                  }}
                />
              )}

              {/* Action buttons overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  p: 0.5,
                }}
              >
                {!image.isPrimary && (
                  <Tooltip title="Set as primary photo">
                    <IconButton
                      size="small"
                      onClick={() => handleSetPrimary(image)}
                      sx={{ bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'white' }, width: 28, height: 28 }}
                    >
                      <StarBorder sx={{ fontSize: 16, color: '#F24F2F' }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Delete photo">
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(image)}
                    disabled={deletingPath === image.path}
                    sx={{ bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'white' }, width: 28, height: 28 }}
                  >
                    {deletingPath === image.path
                      ? <CircularProgress size={14} />
                      : <Delete sx={{ fontSize: 16, color: '#c62828' }} />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        // Show Google photo as fallback preview
        <Box
          sx={{
            border: '2px dashed #ddd',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            mb: 2,
            bgcolor: '#fafafa',
          }}
        >
          {googlePhotoUrl ? (
            <Box>
              <img
                src={googlePhotoUrl}
                alt="Google Places photo"
                style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8, marginBottom: 8 }}
              />
              <Typography variant="caption" color="text.secondary">
                Currently showing Google Places photo. Upload your own to replace it.
              </Typography>
            </Box>
          ) : (
            <Box>
              <AddPhotoAlternate sx={{ fontSize: 48, color: '#ddd', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No photos uploaded yet. Add photos to stand out in JamApp.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Typography variant="caption" color="text.secondary">
        ⭐ The primary photo appears as the main image on your venue card in JamApp.
        {images.length >= MAX_IMAGES && " You've reached the maximum of 6 photos."}
      </Typography>
    </Box>
  );
};

export default VenueImageManager;